import { cloneDeep, find } from 'lodash';
import { AUTO_ACCEPT_ODDS_CHANGE, SHOW_TOTAL_STAKES_TOGGLE } from '../../data-types/betting/bettingEnums';
import {
  APPEND_SYSTEM_BET_STAKE_DETAILS_ARRAY,
  BetActionType,
  BetState,
  DEQUEUE_SUPERCHARGED_BET,
  ENQUEUE_SUPERCHARGED_BET,
  REMOVE_ALL_OUTCOME_SELECTION_BY_EVENT_CHANGES,
  REMOVE_ALL_SYSTEM_BET_STAKE_DETAILS,
  REMOVE_OUTCOME_SELECTION_BY_EVENT_CHANGES,
  REMOVE_SYSTEM_BET_STAKE_DETAILS,
  REMOVE_SYSTEM_BET_STAKE_VALIDATION_STATUS,
  SET_ALL_STAKES_STATUS,
  SET_AUTO_ACCEPT_ODDS_CHANGE,
  SET_AVAILABLE_SYSTEM_BET_TYPES,
  SET_BETSLIP_SNACKBAR,
  SET_IS_PLACING_BET,
  SET_OUTCOME_SELECTION_BY_EVENT_CHANGES,
  SET_PENDING_BETS,
  SET_SUPERCHARGED_BETS,
  SET_SYSTEM_BET_CONFIG,
  SET_SYSTEM_BET_PLACING_ORDER,
  SET_SYSTEM_BET_STAKE_DETAILS,
  SET_SYSTEM_BET_STAKE_VALIDATION_STATUS,
  SET_TRENDING_BETS,
  SET_TRENDING_BETS_CAROUSEL_OPEN,
} from '../types/betTypes';
import { RESET_STATE } from '../types/stateTypes';

export const initialBetState: BetState = {
  pendingBets: [],
  isPlacingBet: false,
  betslipSnackbarOpen: false,
  trendingBets: [],
  trendingBetsCarouselOpen: false,
  autoAcceptOddsChange: AUTO_ACCEPT_ODDS_CHANGE.NO,
  superchargedBets: [],
  showTotalStakesToggleStatus: SHOW_TOTAL_STAKES_TOGGLE.OFF,
  systemBetConfig: undefined,
  systemBetStakesDetails: [],
  availableSystemBetTypes: [],
  systemBetsPlacingOrder: [],
  systemBetStakeValidationStatus: {},
  outcomeSelectionByEventChanged: {},
};

export const betReducer = (state = initialBetState, action: BetActionType): BetState => {
  switch (action.type) {
    case RESET_STATE:
      return {
        ...initialBetState,
      };
    case SET_PENDING_BETS:
      return { ...state, pendingBets: action.meta };
    case SET_IS_PLACING_BET:
      return { ...state, isPlacingBet: action.meta };
    case SET_BETSLIP_SNACKBAR: {
      return { ...state, betslipSnackbarOpen: action.meta };
    }
    case SET_TRENDING_BETS: {
      return { ...state, trendingBets: action.meta };
    }
    case SET_TRENDING_BETS_CAROUSEL_OPEN: {
      return { ...state, trendingBetsCarouselOpen: action.meta };
    }
    case SET_AUTO_ACCEPT_ODDS_CHANGE: {
      return { ...state, autoAcceptOddsChange: action.meta };
    }
    case ENQUEUE_SUPERCHARGED_BET: {
      return { ...state, superchargedBets: [...state.superchargedBets, action.meta] };
    }
    case DEQUEUE_SUPERCHARGED_BET: {
      return { ...state, superchargedBets: state.superchargedBets.slice(1) };
    }
    case SET_SUPERCHARGED_BETS: {
      return { ...state, superchargedBets: action.meta };
    }
    case SET_ALL_STAKES_STATUS: {
      return { ...state, showTotalStakesToggleStatus: action.meta };
    }
    case SET_SYSTEM_BET_CONFIG: {
      return { ...state, systemBetConfig: action.meta };
    }
    case SET_SYSTEM_BET_STAKE_DETAILS: {
      const { systemBetId, amount, isEachWay } = action.meta;
      const currentStakes = state.systemBetStakesDetails;

      const alreadyExists = find(currentStakes, { systemBetId });
      if (alreadyExists) {
        return {
          ...state,
          systemBetStakesDetails: currentStakes.map(systemBetStake => {
            if (systemBetStake.systemBetId === systemBetId) {
              return { ...systemBetStake, amount, isEachWay };
            }

            return systemBetStake;
          }),
        };
      }

      return {
        ...state,
        systemBetStakesDetails: [...currentStakes, action.meta],
      };
    }
    case REMOVE_SYSTEM_BET_STAKE_DETAILS: {
      return {
        ...state,
        systemBetStakesDetails: state.systemBetStakesDetails.filter(systemBetStake => systemBetStake.systemBetId !== action.meta),
      };
    }
    case APPEND_SYSTEM_BET_STAKE_DETAILS_ARRAY: {
      const currentDetails = state.systemBetStakesDetails;
      let clonedRestoredSystemBets = cloneDeep(action.meta);

      const mergedDetails = currentDetails.map(stakeDetails => {
        const restored = find(clonedRestoredSystemBets, { systemBetId: stakeDetails.systemBetId });
        if (restored) {
          clonedRestoredSystemBets = clonedRestoredSystemBets.filter(
            restoredSystemBet => restoredSystemBet.systemBetId !== stakeDetails.systemBetId,
          );
          return { ...stakeDetails, ...restored };
        }
        return stakeDetails;
      });

      return {
        ...state,
        systemBetStakesDetails: [...mergedDetails, ...clonedRestoredSystemBets],
      };
    }
    case REMOVE_ALL_SYSTEM_BET_STAKE_DETAILS: {
      return { ...state, systemBetStakesDetails: [] };
    }
    case SET_AVAILABLE_SYSTEM_BET_TYPES: {
      return { ...state, availableSystemBetTypes: action.meta };
    }
    case SET_SYSTEM_BET_PLACING_ORDER: {
      return { ...state, systemBetsPlacingOrder: action.meta };
    }
    case SET_SYSTEM_BET_STAKE_VALIDATION_STATUS: {
      const mapCopy = cloneDeep(state.systemBetStakeValidationStatus);
      mapCopy[action.meta.key] = action.meta.value;

      return { ...state, systemBetStakeValidationStatus: mapCopy };
    }
    case REMOVE_SYSTEM_BET_STAKE_VALIDATION_STATUS: {
      const mapCopy = cloneDeep(state.systemBetStakeValidationStatus);
      if (mapCopy[action.meta] !== undefined) {
        delete mapCopy[action.meta];
      }

      return { ...state, systemBetStakeValidationStatus: mapCopy };
    }
    case SET_OUTCOME_SELECTION_BY_EVENT_CHANGES: {
      return { ...state, outcomeSelectionByEventChanged: { ...state.outcomeSelectionByEventChanged, ...action.meta } };
    }
    case REMOVE_OUTCOME_SELECTION_BY_EVENT_CHANGES: {
      const { outcomeSelectionByEventChanged } = state;

      const clonedTracker = cloneDeep(outcomeSelectionByEventChanged);
      const eventId = action.meta;
      delete clonedTracker[eventId];

      return { ...state, outcomeSelectionByEventChanged: { ...clonedTracker } };
    }
    case REMOVE_ALL_OUTCOME_SELECTION_BY_EVENT_CHANGES: {
      return { ...state, outcomeSelectionByEventChanged: {} };
    }
    default:
      return state;
  }
};
